import { useState, useEffect, useCallback } from "react";
import { MapLayerType, LayersConfig } from "../types";
import { LayerService } from "../services/layerService";

const LAYERS_CONFIG_STORAGE_KEY = "mapMonitoring_layersConfig";

export function useMapLayers() {
    const loadLayersConfigFromStorage = (): LayersConfig => {
        const defaults = LayerService.createDefaultLayersConfig();

        try {
            const stored = localStorage.getItem(LAYERS_CONFIG_STORAGE_KEY);
            if (stored) {
                const parsed = JSON.parse(stored) as Partial<LayersConfig>;


                const normalized = { ...defaults } as LayersConfig;

                (Object.keys(defaults) as MapLayerType[]).forEach((layerType) => {
                    const fromStorage = parsed?.[layerType];

                    // если слой в сторадже есть и это объект — мёрджим поля,
                    // иначе оставляем дефолт
                    if (fromStorage && typeof fromStorage === "object") {
                        normalized[layerType] = {
                            ...defaults[layerType],
                            ...fromStorage,
                            // страховка: type всегда должен быть корректным
                            type: layerType,
                            // страховка: name если вдруг стёрли — восстановим дефолт
                            name:
                                (fromStorage as any).name ||
                                defaults[layerType].name ||
                                LayerService.getDefaultLayerName(layerType),
                            // страховка: visible если вдруг не boolean
                            visible:
                                typeof (fromStorage as any).visible === "boolean"
                                    ? (fromStorage as any).visible
                                    : defaults[layerType].visible,
                            // страховка: zIndex если вдруг не number
                            zIndex:
                                typeof (fromStorage as any).zIndex === "number"
                                    ? (fromStorage as any).zIndex
                                    : defaults[layerType].zIndex,
                        };
                    }
                });

                return normalized;
            }
        } catch (error) {
            console.error("Ошибка при загрузке конфигурации слоев из LocalStorage:", error);
        }

        return defaults;
    };

    const [layersConfig, setLayersConfig] = useState<LayersConfig>(
        loadLayersConfigFromStorage
    );

    useEffect(() => {
        try {
            localStorage.setItem(
                LAYERS_CONFIG_STORAGE_KEY,
                JSON.stringify(layersConfig)
            );
        } catch (error) {
            console.error("Ошибка при сохранении конфигурации слоев в LocalStorage:", error);
        }
    }, [layersConfig]);

    const toggleLayerVisibility = useCallback((layerType: MapLayerType) => {
        setLayersConfig((prev) => {
            let newConfig = { ...prev };
            
            // Если переключаем restrictedZones, одновременно переключаем drawnShapes
            if (layerType === "restrictedZones") {
                const newRestrictedVisibility = !prev[layerType].visible;
                newConfig = LayerService.updateLayerVisibility(prev, "restrictedZones", newRestrictedVisibility);
                // Переключаем drawnShapes в то же состояние
                newConfig = LayerService.updateLayerVisibility(newConfig, "drawnShapes", newRestrictedVisibility);
            } else {
                // Для остальных слоев обычная логика
                newConfig = LayerService.updateLayerVisibility(prev, layerType, !prev[layerType].visible);
            }
            
            return newConfig;
        });
    }, []);

    const setLayerVisibility = useCallback((layerType: MapLayerType, visible: boolean) => {
        setLayersConfig((prev) => LayerService.updateLayerVisibility(prev, layerType, visible));
    }, []);

    const getLayerVisibility = useCallback(
        (layerType: MapLayerType): boolean => {
            return layersConfig[layerType].visible;
        },
        [layersConfig]
    );

    const setLayerZIndex = useCallback((layerType: MapLayerType, zIndex: number) => {
        setLayersConfig((prev) => LayerService.updateLayerZIndex(prev, layerType, zIndex));
    }, []);

    const getLayerZIndex = useCallback(
        (layerType: MapLayerType): number => {
            return layersConfig[layerType].zIndex;
        },
        [layersConfig]
    );

    const getLayersOrder = useCallback((): MapLayerType[] => {
        return LayerService.getLayersOrder(layersConfig);
    }, [layersConfig]);

    const resetLayersConfig = useCallback(() => {
        setLayersConfig(LayerService.createDefaultLayersConfig());
    }, []);

    const getLayerConfig = useCallback(
        (layerType: MapLayerType) => {
            return layersConfig[layerType];
        },
        [layersConfig]
    );

    return {
        layersConfig,
        toggleLayerVisibility,
        setLayerVisibility,
        getLayerVisibility,
        setLayerZIndex,
        getLayerZIndex,
        getLayersOrder,
        resetLayersConfig,
        getLayerConfig,
    };
}
